import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import MainLayout from '../../layout/main-layout';
import { APP_API_URL } from '../../utils';

const money = (value) =>
  `${new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(Math.round(Number(value) || 0))} F`;

const currentPeriod = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const monthLabel = (period) => {
  const [year, month] = String(period || '').split('-');
  if (!year || !month) return period;
  return new Date(Number(year), Number(month) - 1, 1).toLocaleDateString('fr-FR', { month: 'long', year: 'numeric' });
};

const retenueLabel = (adjustment) => {
  if (!adjustment) return '—';
  const parts = [];
  if (adjustment.ne_pas_payer) parts.push('Non payé');
  if (Number(adjustment.jours_faute) > 0) parts.push(`${adjustment.jours_faute} j. de faute`);
  if (Number(adjustment.amende) > 0) parts.push(`Amende ${money(adjustment.amende)}`);
  if (adjustment.motif && parts.length) parts.push(adjustment.motif);
  return parts.length ? parts.join(' · ') : '—';
};

const emptyAdjustment = { ne_pas_payer: false, jours_faute: 0, amende: 0, motif: '' };

export default function PaieMois() {
  const [searchParams, setSearchParams] = useSearchParams();
  const period = searchParams.get('mois') || currentPeriod();
  const setPeriod = (value) => setSearchParams({ mois: value });
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [openId, setOpenId] = useState(null);
  const [draft, setDraft] = useState(emptyAdjustment);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${APP_API_URL}/payslips`, {
        params: { period },
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows(response.data.rows || []);
    } catch (err) {
      setError('Impossible de charger la paie du mois.');
      setRows([]);
    } finally {
      setIsLoading(false);
    }
  }, [period]);

  useEffect(() => {
    load();
  }, [load]);

  const saved = useMemo(() => rows.filter((row) => row.payslip_id), [rows]);
  const totalNet = saved.reduce((sum, row) => sum + Number(row.net || 0), 0);
  const label = monthLabel(period);

  const generate = async () => {
    if (!window.confirm(`Générer ou mettre à jour les fiches de ${label} ?`)) return;
    setGenerating(true);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${APP_API_URL}/payslips/generate`,
        { period },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const skipped = response.data.skipped || [];
      setMessage(
        skipped.length
          ? `${response.data.message} Salaire de base manquant : ${skipped.join(', ')}.`
          : response.data.message
      );
      await load();
    } catch (err) {
      setError('Génération impossible.');
    } finally {
      setGenerating(false);
    }
  };

  const openAdjustment = (row) => {
    setOpenId(row.personnel_id);
    setDraft({ ...emptyAdjustment, ...(row.adjustment || {}) });
  };

  const saveAdjustment = async (personnelId) => {
    setSavingId(personnelId);
    setError('');
    setMessage('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${APP_API_URL}/payslips/adjustment`,
        {
          personnel_id: personnelId,
          period,
          ne_pas_payer: Boolean(draft.ne_pas_payer),
          jours_faute: Number(draft.jours_faute) || 0,
          amende: Number(draft.amende) || 0,
          motif: draft.motif || '',
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setMessage(response.data.message);
      setOpenId(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.message || 'Retenue impossible à enregistrer.');
    } finally {
      setSavingId(null);
    }
  };

  const printRow = (personnelId) => {
    window.open(`/personnel/paie/${personnelId}?mois=${period}&print=1`, '_blank', 'noopener');
  };

  return (
    <MainLayout>
      <div className="duo-page">
        <div className="duo-hero duo-hero--compact mb-3">
          <p className="duo-hero-sub mb-1">Ressources humaines</p>
          <h1 className="duo-hero-title">Paie du mois</h1>
          <p className="duo-hero-sub mt-2 mb-0">
            Générez les fiches de <span className="text-capitalize">{label}</span>, puis imprimez-les ligne par ligne.
            Une amende, des jours de faute ou un mois non payé se règlent avant la génération.
          </p>
        </div>

        <div className="duo-kpi-row">
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Salariés actifs</p>
            <p className="duo-kpi-chip__value">{rows.length}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Bulletins enregistrés</p>
            <p className="duo-kpi-chip__value">{saved.length}</p>
          </div>
          <div className="duo-kpi-chip">
            <p className="duo-kpi-chip__label">Net enregistré</p>
            <p className="duo-kpi-chip__value">{money(totalNet)}</p>
          </div>
        </div>

        <section className="duo-panel">
          <div className="duo-toolbar">
            <label className="d-flex align-items-center gap-2 mb-0">
              Mois
              <input type="month" className="duo-input" value={period} onChange={(e) => setPeriod(e.target.value)} />
            </label>
            <button type="button" className="duo-btn duo-btn--primary" onClick={generate} disabled={generating || isLoading}>
              {generating ? 'Génération…' : 'Générer les fiches du mois'}
            </button>
            <Link to="/personnel" className="duo-btn duo-btn--ghost">Personnel</Link>
            <Link to="/personnel/paie/reglages" className="duo-btn duo-btn--ghost">Signature et cachet</Link>
          </div>
          {error && <p className="text-danger px-3">{error}</p>}
          {message && <p className="text-success px-3">{message}</p>}
          <div className="duo-table-wrap">
            <table className="duo-table">
              <thead>
                <tr>
                  <th>Mois</th>
                  <th>Salarié</th>
                  <th>Fonction</th>
                  <th>Retenue</th>
                  <th>Brut</th>
                  <th>Net</th>
                  <th>Bulletin</th>
                </tr>
              </thead>
              <tbody>
                {isLoading ? (
                  <tr>
                    <td colSpan={7} className="duo-empty-row">Chargement de la paie…</td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="duo-empty-row">Aucun salarié actif.</td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <React.Fragment key={row.personnel_id}>
                      <tr>
                        <td className="text-capitalize">{label}</td>
                        <td className="fw-bold">{row.name}</td>
                        <td>{row.poste || '—'}</td>
                        <td>{retenueLabel(row.adjustment)}</td>
                        <td>{row.payslip_id ? money(row.brut) : '—'}</td>
                        <td>{row.payslip_id ? money(row.net) : '—'}</td>
                        <td>
                          <div className="duo-table__actions">
                            <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => openAdjustment(row)}>
                              Retenue
                            </button>
                            <Link className="duo-btn duo-btn--ghost duo-btn--sm" to={`/personnel/paie/${row.personnel_id}?mois=${period}`}>
                              {row.payslip_id ? 'Voir' : 'Calculer'}
                            </Link>
                            <button
                              type="button"
                              className="duo-btn duo-btn--ghost duo-btn--sm"
                              disabled={!row.payslip_id}
                              onClick={() => printRow(row.personnel_id)}
                            >
                              Imprimer
                            </button>
                          </div>
                        </td>
                      </tr>
                      {openId === row.personnel_id && (
                        <tr>
                          <td colSpan={7}>
                            <div className="d-flex flex-wrap gap-3 align-items-end py-2">
                              <label className="d-flex align-items-center gap-2 mb-0">
                                <input
                                  type="checkbox"
                                  checked={Boolean(draft.ne_pas_payer)}
                                  onChange={(e) => setDraft((current) => ({ ...current, ne_pas_payer: e.target.checked }))}
                                />
                                Ne pas payer ce mois
                              </label>
                              <label className="mb-0">
                                Jours de faute
                                <input
                                  type="number"
                                  min="0"
                                  max="30"
                                  className="form-control"
                                  value={draft.jours_faute}
                                  onChange={(e) => setDraft((current) => ({ ...current, jours_faute: e.target.value }))}
                                />
                              </label>
                              <label className="mb-0">
                                Amende (FCFA)
                                <input
                                  type="number"
                                  min="0"
                                  step="1"
                                  className="form-control"
                                  value={draft.amende}
                                  onChange={(e) => setDraft((current) => ({ ...current, amende: e.target.value }))}
                                />
                              </label>
                              <label className="mb-0" style={{ minWidth: 220 }}>
                                Motif
                                <input
                                  type="text"
                                  className="form-control"
                                  value={draft.motif}
                                  onChange={(e) => setDraft((current) => ({ ...current, motif: e.target.value }))}
                                  placeholder="Faute lourde, absence…"
                                />
                              </label>
                              <button type="button" className="duo-btn duo-btn--primary duo-btn--sm" disabled={savingId === row.personnel_id} onClick={() => saveAdjustment(row.personnel_id)}>
                                {savingId === row.personnel_id ? 'Enregistrement…' : 'Enregistrer la retenue'}
                              </button>
                              <button type="button" className="duo-btn duo-btn--ghost duo-btn--sm" onClick={() => setOpenId(null)}>
                                Fermer
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </MainLayout>
  );
}
